import {
  COST_BANDS,
  FOOD_ACTIVITY_FILTERS,
  FOOD_DIET_CLASSES,
  FOOD_HOUSEHOLD_PORTION_FILTERS,
  FOOD_KINDS,
  FOOD_ORDERS,
  FOOD_SOURCES,
  FOOD_VERIFICATION_FILTERS,
  FOOD_VIEWS,
  MEAL_ROLES,
  PREP_TIME_BANDS,
} from '@steado/admin-contract/foods';
import { z } from 'zod';

import { OPTIONAL_FOOD_COLUMN_KEYS, type OptionalFoodColumnKey } from './foods-columns';

/**
 * Filters, the visible columns and the paging cursor all live in the URL, so a
 * half-triaged review queue is a link an operator can hand to someone else.
 * The open record used to as well; it is its own route now.
 *
 * Every field `.catch(undefined)`: a stale bookmark naming a filter that was
 * renamed degrades to the unfiltered catalog rather than an error page.
 */
export const foodsSearchSchema = z.object({
  view: z.enum(FOOD_VIEWS).default('catalog').catch('catalog'),
  search: z.string().trim().min(1).max(120).optional().catch(undefined),
  kind: z.enum(FOOD_KINDS).optional().catch(undefined),
  source: z.enum(FOOD_SOURCES).optional().catch(undefined),
  dietClass: z.enum(FOOD_DIET_CLASSES).optional().catch(undefined),
  mealRole: z.enum(MEAL_ROLES).optional().catch(undefined),
  prepTimeBand: z.enum(PREP_TIME_BANDS).optional().catch(undefined),
  costBand: z.enum(COST_BANDS).optional().catch(undefined),
  verification: z.enum(FOOD_VERIFICATION_FILTERS).optional().catch(undefined),
  activity: z.enum(FOOD_ACTIVITY_FILTERS).optional().catch(undefined),
  householdPortion: z.enum(FOOD_HOUSEHOLD_PORTION_FILTERS).optional().catch(undefined),
  order: z.enum(FOOD_ORDERS).optional().catch(undefined),
  cursor: z.string().optional().catch(undefined),
  /**
   * Which optional columns are showing.
   *
   * In the URL like every filter on this screen: "the catalog with meal role
   * and portions visible, filtered to unclassified cuisines" is a view worth
   * being able to send somebody. An unknown key is dropped rather than
   * throwing, same as every other field here.
   *
   * Read as either a comma list or an array. The screen hands the router an
   * array, and the router's default search serialiser writes an array to the
   * URL as JSON and reads it back as one, so a schema that only understood
   * the comma form saw an array, failed, and `.catch` turned every toggle
   * into nothing: the checkbox never ticked and the column never appeared.
   * A hand-typed `?cols=mealRole,portionCount` still works.
   */
  cols: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) =>
      value === undefined
        ? undefined
        : (Array.isArray(value) ? value : value.split(',')).filter((key): key is OptionalFoodColumnKey =>
            (OPTIONAL_FOOD_COLUMN_KEYS as readonly string[]).includes(key),
          ),
    )
    .catch(undefined),
});

export type FoodsSearch = z.infer<typeof foodsSearchSchema>;

/**
 * The review queue is the same endpoint with different defaults, not a second
 * one: unverified rows the pipeline estimated, oldest first, because a queue is
 * worked from the front. An operator can still widen the filters from there —
 * the view sets the starting point, it does not lock it.
 */
export function viewDefaults(view: FoodsSearch['view']): Partial<FoodsSearch> {
  if (view === 'review') {
    return { verification: 'unverified', source: 'ai_estimated', order: 'oldest', activity: 'active' };
  }

  return { verification: undefined, source: undefined, order: undefined, activity: undefined };
}
